import React from "react";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";
import { Linkedin, Mail, Globe, ArrowUpRight } from "lucide-react";

export const Footer = () => {
  const { t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-white/10 bg-[#080809] relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none"></div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Logo size="lg" />
            <p className="mt-6 text-zinc-400 max-w-md text-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-linkedin"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-bicenter-cyan transition-all"
              >
                <Linkedin size={16} />
              </a>
              <a
                href="mailto:connect@bicenter-ai.com"
                data-testid="footer-email"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-bicenter-violet transition-all"
              >
                <Mail size={16} />
              </a>
              <a
                href="#top"
                data-testid="footer-website"
                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-bicenter-cyan transition-all"
              >
                <Globe size={16} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-zinc-500 mb-4">{t.footer.sections.product}</p>
              <ul className="space-y-3">
                <li><a href="#solutions" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.solutions}</a></li>
                <li><a href="#demo" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.demo}</a></li>
                <li><a href="#access" className="text-sm text-zinc-300 hover:text-white transition">Access</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-zinc-500 mb-4">{t.footer.sections.platforms}</p>
              <ul className="space-y-3">
                <li><a href="https://model.bicenter-ai.com" target="_blank" rel="noreferrer" data-testid="footer-platform-model" className="text-sm text-zinc-300 hover:text-white transition">BICENTER AI Model</a></li>
                <li><a href="https://automation.bicenter-ai.com" target="_blank" rel="noreferrer" data-testid="footer-platform-automation" className="text-sm text-zinc-300 hover:text-white transition">BICENTER AI Automation</a></li>
                <li><a href="https://decision.bicenter-ai.com" target="_blank" rel="noreferrer" data-testid="footer-platform-decision" className="text-sm text-zinc-300 hover:text-white transition">BICENTER AI Décision</a></li>
                <li><a href="https://rh.bicenter-ai.com" target="_blank" rel="noreferrer" data-testid="footer-platform-rh" className="text-sm text-zinc-300 hover:text-white transition">BICENTER AI RH</a></li>
                <li><a href="https://leadcard.bicenter-ai.com" target="_blank" rel="noreferrer" data-testid="footer-platform-leadcard" className="text-sm text-zinc-300 hover:text-white transition">BICENTER AI Leadcard</a></li>
                <li><a href="https://speaker.bicenter-ai.com" target="_blank" rel="noreferrer" data-testid="footer-platform-speaker" className="text-sm text-zinc-300 hover:text-white transition">BICENTER AI Speaker</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-zinc-500 mb-4">{t.footer.sections.events}</p>
              <ul className="space-y-3">
                <li><a href="#events" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.webinars}</a></li>
                <li><a href="#events" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.masterclass}</a></li>
                <li><a href="#events" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.conferences}</a></li>
                <li><a href="#events" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.diners}</a></li>
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.32em] text-zinc-500 mb-4">{t.footer.sections.company}</p>
              <ul className="space-y-3">
                <li><a href="#roadmap" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.roadmap}</a></li>
                <li><a href="#join" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.join}</a></li>
                <li><a href="#contact" className="text-sm text-zinc-300 hover:text-white transition">{t.footer.links.contact}</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-zinc-500">
            © {year} BICENTER AI. {t.footer.rights}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
            <a href="/legal#mentions-legales" data-testid="footer-legal" className="hover:text-white transition">{t.footer.links.legal}</a>
            <a href="/legal#confidentialite" data-testid="footer-privacy" className="hover:text-white transition">{t.footer.links.privacy}</a>
            <a href="/legal#cgv" data-testid="footer-cgv" className="hover:text-white transition">{t.footer.links.terms}</a>
            <a href="/legal#cookies" data-testid="footer-cookies" className="hover:text-white transition">{t.footer.links.cookies}</a>
            <a
              href="mailto:connect@bicenter-ai.com"
              className="copyable inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition group"
            >
              connect@bicenter-ai.com
              <ArrowUpRight size={12} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
